import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import { initGA, pageView } from "./utils/analytics";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Dashboard from "./pages/Dashboard";
import Assessments from "./pages/Assessments";
import AssessmentDetail from "./pages/AssessmentDetail";
import CreateAssessment from "./pages/CreateAssessment";
import Candidates from "./pages/Candidates";
import CandidateDetail from "./pages/CandidateDetail";
import Credits from "./pages/Credits";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Support from "./pages/Support";
import Tutorials from "./pages/Tutorials";
import AssessmentInterface from "./pages/AssessmentInterface";
import CandidateLanding from "./pages/CandidateLanding";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import Terms from "./pages/Terms";
import Pricing from "./pages/Pricing";
import HomePage from "./pages/HomePage";
import RequestDemo from "./pages/RequestDemo";
import AuthCallbackPage from "./pages/auth/AuthCallbackPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import { AuthProvider } from "./components/auth/AuthContext";
import { AuthGuard } from "./components/auth/AuthGuard";
import BlogAuthGuard from "./components/auth/BlogAuthGuard";
import CreditManagement from "./pages/admin/credits";
import Management from "./pages/admin/Management";
import ErrorsAdmin from "./pages/admin/errors";
import Reports from "./pages/admin/Reports";
import AgentTest from './pages/AgentTest';
import ErrorBoundary from './components/ErrorBoundary';
import PublicCandidateView from './pages/PublicCandidateView';
import KnitTest from './pages/KnitTest';
import AtsJobs from './pages/AtsJobs';
import AnalyticsTest from './pages/AnalyticsTest';
import ATSIntegrations from './pages/ATSIntegrations';
import Onboarding from './pages/Onboarding';
import InterviewPractice from './pages/InterviewPractice';
import PracticeHistory from './pages/PracticeHistory';
import RecruiterVerification from "./pages/admin/RecruiterVerification";
import EmailVerification from "./pages/EmailVerification";
import SalesPitchDeck from './pages/SalesPitchDeck';

// Blog Pages
import AdminBlogManagement from './pages/admin/blogs';
import BlogEditor from './pages/admin/blogs/editor';
import BlogCategoryManagement from './pages/admin/blogs/categories';
import BlogPost from './pages/BlogPost';
import BlogListing from './pages/BlogListing';
import PracticeLanding from './pages/PracticeLanding';
import ShortUrlRedirect from './pages/ShortUrlRedirect';
import PersonalizedLanding from './pages/PersonalizedLanding';
import PersonalizedHomePage from './pages/PersonalizedHomePage';
import { HelmetProvider } from 'react-helmet-async';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Replace with your actual Google Analytics measurement ID
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-MBCMHDR8C5';

// Scroll restoration component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Analytics tracking component
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    pageView(location.pathname + location.search);
  }, [location]);

  return null;
};

// Blog Preview Wrapper Component
const BlogPreviewWrapper = () => {
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  
  useEffect(() => {
    console.log('BlogPreviewWrapper mounted');
    console.log('Current path:', location.pathname);
    console.log('Blog ID from path:', id);
    
    // Check if the route is properly mapped
    if (!id) {
      console.error('No ID detected in the URL parameters');
    }
  }, [location.pathname, id]);
  
  if (!id) {
    return <div>Error: No blog ID found in the URL</div>;
  }
  
  return (
    <>
      <div style={{ 
        background: '#4B0082', 
        color: 'white', 
        padding: '8px 16px', 
        textAlign: 'center',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000
      }}>
        Admin Preview Mode - ID: {id}
      </div>
      <div style={{ marginTop: '36px' }}>
        <BlogPost useIdInsteadOfSlug={true} />
      </div>
    </>
  );
};

// Diagnostic component to verify routing works
const DiagnosticComponent = () => {
  const params = useParams();
  const location = useLocation();
  
  return (
    <div style={{ padding: '20px', fontFamily: 'monospace' }}>
      <h1>Route Diagnostic</h1>
      <h2>Current Path: {location.pathname}</h2>
      <h3>URL Parameters:</h3>
      <pre>{JSON.stringify(params, null, 2)}</pre>
      <p>If you can see this page, routing is working properly.</p>
      <button onClick={() => window.history.back()}>Go Back</button>
    </div>
  );
};

const App = () => {
  // Initialize Google Analytics
  useEffect(() => {
    // Create and inject the Google Analytics scripts dynamically
    const loadGoogleAnalytics = (measurementId: string) => {
      // Add the first script tag
      const script1 = document.createElement('script');
      script1.async = true;
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(script1);

      // Initialize dataLayer and define gtag function
      window.dataLayer = window.dataLayer || [];
      function gtag(...args: any[]) {
        window.dataLayer.push(arguments);
      }
      gtag('js', new Date());
      gtag('config', measurementId);

      // Make gtag accessible globally
      window.gtag = gtag;
    };

    // Load Google Analytics with the measurement ID
    initGA(GA_MEASUREMENT_ID);
    loadGoogleAnalytics(GA_MEASUREMENT_ID);
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <BrowserRouter>
            <AuthProvider>
              <TooltipProvider>
                <ScrollToTop />
                <AnalyticsTracker />
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/sign-in" element={<Index />} />
                  <Route path="/home" element={<Navigate to="/" replace />} />
                  <Route path="/auth/callback" element={<AuthCallbackPage />} />
                  <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/verify-email/:token" element={<EmailVerification />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/request-demo" element={<RequestDemo />} />
                  <Route path="/tutorials" element={<Tutorials />} />
                  <Route path="/candidate/:id" element={<PublicCandidateView />} />
                  <Route path="/analytics-test" element={<AnalyticsTest />} />
                  <Route path="/ats-integrations" element={<ATSIntegrations />} />
                  <Route path="/sales-pitch-deck" element={<SalesPitchDeck />} />
                  
                  {/* Blog routes */}
                  <Route path="/blog" element={<BlogListing />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/blog/category/:slug" element={<BlogListing />} />
                  
                  {/* Interview Practice routes */}
                  <Route path="/practice" element={<PracticeLanding />} />
                  <Route path="/practice-start" element={<InterviewPractice />} />
                  
                  {/* Assessment routes */}
                  <Route path="/assessment/:id/landing" element={<CandidateLanding />} />
                  <Route path="/assessment/:id/interface" element={<AssessmentInterface />} />
                  
                  {/* Short URL redirect */}
                  <Route path="/s/:shortId" element={<ShortUrlRedirect />} />
                  
                  {/* Personalized landing pages */}
                  <Route path="/p/:shortId" element={<PersonalizedHomePage />} />

                  {/* Protected routes */}
                  <Route path="/dashboard" element={<AuthGuard><Dashboard /></AuthGuard>} />
                  <Route path="/onboarding" element={<AuthGuard><Onboarding /></AuthGuard>} />
                  <Route path="/assessments" element={<AuthGuard><Assessments /></AuthGuard>} />
                  <Route path="/assessments/create" element={<AuthGuard><CreateAssessment /></AuthGuard>} />
                  <Route path="/assessments/:id" element={<AuthGuard><AssessmentDetail /></AuthGuard>} />
                  <Route path="/candidates" element={<AuthGuard><Candidates /></AuthGuard>} />
                  <Route path="/candidates/:id" element={<AuthGuard><CandidateDetail /></AuthGuard>} />
                  <Route path="/credits" element={<AuthGuard><Credits /></AuthGuard>} />
                  <Route path="/profile" element={<AuthGuard><Profile /></AuthGuard>} />
                  <Route path="/settings" element={<AuthGuard><Settings /></AuthGuard>} />
                  <Route path="/support" element={<AuthGuard><Support /></AuthGuard>} />
                  <Route path="/practice-history" element={<AuthGuard><PracticeHistory /></AuthGuard>} />
                  
                  {/* Admin routes */}
                  <Route path="/admin/credits" element={<AuthGuard><CreditManagement /></AuthGuard>} />
                  <Route path="/admin/management" element={<AuthGuard><Management /></AuthGuard>} />
                  <Route path="/admin/users" element={<Navigate to="/admin/management" replace />} />
                  <Route path="/admin/prospects" element={<Navigate to="/admin/management" replace />} />
                  <Route path="/admin/reports" element={<AuthGuard><Reports /></AuthGuard>} />
                  <Route path="/admin/errors" element={<AuthGuard><ErrorsAdmin /></AuthGuard>} />
                  <Route path="/admin/recruiter-verification" element={<AuthGuard><RecruiterVerification /></AuthGuard>} />
                  
                  {/* Blog Admin routes */}
                  <Route path="/admin/blogs" element={<BlogAuthGuard><AdminBlogManagement /></BlogAuthGuard>} />
                  <Route path="/admin/blogs/new" element={<BlogAuthGuard><BlogEditor /></BlogAuthGuard>} />
                  <Route path="/admin/blogs/edit/:id" element={<BlogAuthGuard><BlogEditor /></BlogAuthGuard>} />
                  <Route path="/admin/blogs/categories" element={<BlogAuthGuard><BlogCategoryManagement /></BlogAuthGuard>} />
                  <Route path="/admin/blogs/preview/:id" element={
                    <BlogAuthGuard>
                      <BlogPreviewWrapper />
                    </BlogAuthGuard>
                  } />
                  <Route path="/admin/blogs/diagnostic/:id" element={<DiagnosticComponent />} />
                  
                  <Route path="/agent-test" element={<AuthGuard><AgentTest /></AuthGuard>} />
                  <Route path="/knit-test" element={<AuthGuard><KnitTest /></AuthGuard>} />
                  <Route path="/ats-jobs" element={<AuthGuard><AtsJobs /></AuthGuard>} />

                  {/* 404 */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </TooltipProvider>
              <Toaster />
              <Sonner />
            </AuthProvider>
          </BrowserRouter>
        </HelmetProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
